import { cn, Textarea } from "@aircall/ds";
import { useControllableState } from "@aircall/hooks";
import {
  type ChangeEvent,
  type ComponentPropsWithoutRef,
  type FocusEvent,
  type ForwardedRef,
  forwardRef,
  type KeyboardEvent,
  useEffect,
  useRef,
} from "react";

import {
  type EditableValidationStatus,
  editableFieldVariants,
} from "./Editable.variants";

export interface EditableTextareaProps
  extends Omit<
    ComponentPropsWithoutRef<"textarea">,
    "value" | "defaultValue" | "onChange" | "onSubmit"
  > {
  /** Controlled value. */
  value?: string;
  /** Uncontrolled initial value. */
  defaultValue?: string;
  /** Fired on every keystroke (controllable state change). */
  onChange?: (value: string) => void;
  /** Fired with the trimmed value when the field is committed (blur / Enter). */
  onSubmit?: (value: string) => void;
  /** Visual validation state of the inline field. */
  validationStatus?: EditableValidationStatus;
  /** Extra classes merged onto the textarea (e.g. typography). */
  className?: string;
  "data-test"?: string;
}

function autoGrow(textarea: HTMLTextAreaElement) {
  textarea.style.height = "auto";
  textarea.style.height = `${textarea.scrollHeight}px`;
}

/**
 * Inline-editable textarea: looks like plain text, auto-grows to fit content,
 * and commits the trimmed value on blur and on Enter (Shift+Enter inserts a
 * newline). Composes the DS `Textarea` primitive.
 *
 * A structureless leaf (single field, no sub-parts to assemble) → follows the
 * leaf-uses-variants rule. Styling comes from the shared `editableFieldVariants`
 * helper merged via `cn`; state is exposed through `data-slot` / `data-*`.
 *
 * The DS `Textarea` is a plain function (not a `forwardRef` component), so the
 * underlying node is reached through a `display: contents` wrapper. That wrapper
 * gives a reliable handle both to size pre-filled content on mount and to
 * forward a `ref` to the actual `<textarea>` to the consumer.
 */
export const EditableTextarea = forwardRef<
  HTMLTextAreaElement,
  EditableTextarea.Props
>(
  (
    componentProps: EditableTextarea.Props,
    forwardRef: ForwardedRef<HTMLTextAreaElement>
  ) => {
    const {
      value: valueProp,
      defaultValue,
      onChange,
      onSubmit,
      onBlur,
      onKeyDown,
      validationStatus = "success",
      className,
      rows = 1,
      "data-test": dataTest = "editable-textarea",
      ...props
    } = componentProps;

    const containerRef = useRef<HTMLDivElement | null>(null);

    const [value, setValue] = useControllableState<string>({
      prop: valueProp,
      defaultProp: defaultValue ?? "",
      onChange,
    });

    // Re-fit the height on mount (pre-filled content) and on every value change.
    // The same effect forwards the resolved node to the consumer's ref so the
    // leaf behaves like a real `forwardRef` despite the non-forwardRef primitive.
    useEffect(() => {
      /* v8 ignore next -- defensive: the wrapper always renders one textarea */
      const node = containerRef.current?.querySelector("textarea") ?? null;
      if (node) {
        autoGrow(node);
      }
      if (typeof forwardRef === "function") {
        forwardRef(node);
      } else if (forwardRef) {
        forwardRef.current = node;
      }
    }, [value, forwardRef]);

    function handleChange(event: ChangeEvent<HTMLTextAreaElement>) {
      setValue(event.target.value);
      autoGrow(event.currentTarget);
    }

    function handleBlur(event: FocusEvent<HTMLTextAreaElement>) {
      const trimmed = event.currentTarget.value.trim();
      // Keep the displayed value in sync with what we commit, so trailing
      // whitespace doesn't linger in the field after submit.
      setValue(trimmed);
      onSubmit?.(trimmed);
      onBlur?.(event);
    }

    function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        // Dropping focus triggers onBlur, which commits the value via onSubmit.
        event.currentTarget.blur();
      }
      onKeyDown?.(event);
    }

    return (
      <div
        ref={containerRef}
        data-slot="editable-textarea"
        className="contents"
      >
        <Textarea
          {...props}
          data-slot="editable-textarea-field"
          data-test={dataTest}
          data-validation-status={validationStatus}
          rows={rows}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={cn(
            editableFieldVariants({ validationStatus }),
            "min-h-0 resize-none overflow-hidden border-none shadow-none focus-visible:border-none",
            className
          )}
        />
      </div>
    );
  }
);

EditableTextarea.displayName = "EditableTextarea";

export namespace EditableTextarea {
  export type Props = EditableTextareaProps;
}
